import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Package, Layers, ArrowLeft, Award } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/navigation/Breadcrumb';
import { BrochureCTA } from '../components/products/ComplianceBlock';
import productsData from '../data/completeProductsData';
import { trackPageView } from '../utils/analytics';

// Reverse index: product image path -> { name, path } so the System Gallery is clickable & informative
const PRODUCT_IMAGE_INDEX = (() => {
  const cats = [
    productsData.laryngoStroboscopyProcessors,
    productsData.surgeryProcessors,
    productsData.cameraHeads,
    productsData.flexibleEndoscopes,
    productsData.fibreOpticEndoscopes,
    productsData.compactCameras,
    productsData.rigidEndoscopes,
    productsData.illuminationSystems,
    productsData.documentationSystems,
    productsData.medicalMonitors,
    productsData.softwareSystems,
  ];
  const idx = {};
  cats.forEach((cat) => {
    (cat?.products || []).forEach((p) => {
      (p.images || []).forEach((img) => {
        if (img && !idx[img]) idx[img] = { name: p.name, path: `/products/${cat.slug}/${p.slug}` };
      });
    });
  });
  return idx;
})();

const SystemDetailPage = () => {
  const { systemSlug } = useParams();
  
  useEffect(() => {
    trackPageView(`System: ${systemSlug}`);
  }, [systemSlug]);

  // Find system data
  const getSystemData = () => {
    const systems = [
      productsData.endoscopyTowerSystem,
      productsData.endoSTROBESystem,
      productsData.endoFLEXSpectarSystem,
      productsData.endoCOMPACTVeezarSystem,
      productsData.endoOFFICESpectarSystem,
      productsData.endoOFFICEVeezarSystem,
      productsData.endoSURGERYSpectarSystem,
      productsData.endoSURGERY3DSpectarSystem,
      productsData.endoPORTABLEVeezarSystem
    ];
    return systems.find(sys => sys.slug === systemSlug);
  };

  const system = getSystemData();

  if (!system) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-black pt-32 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-white mb-4">System Not Found</h1>
            <Link to="/system-solutions" className="btn-primary">
              Back to System Solutions
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${system.name} - XION Medical Philippines`}
        description={system.fullDescription || system.description}
        path={`/system-solutions/${systemSlug}`}
      />
      <Header />
      <main className="min-h-screen bg-hud pt-32 px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <Breadcrumb 
            items={[
              { label: 'System Solutions', link: '/system-solutions' },
              { label: system.name }
            ]}
          />

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16"
          >
            <div className="inline-block px-4 py-1 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full mb-4">
              <span className="text-[#00FFD1] text-sm font-mono">{system.category}</span>
            </div>
            <h1 className="text-6xl font-bold text-white mb-4">{system.name}</h1>
            <p className="text-2xl text-[#00FFD1] mb-6">{system.tagline}</p>
            {system.highlight && (
              <div
                data-testid={`system-detail-highlight-${system.slug}`}
                className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-[#F08A24]/10 border border-[#F08A24]/40 rounded-full"
              >
                <Award size={18} className="text-[#F08A24] shrink-0" />
                <span className="text-[#F08A24] text-sm font-bold uppercase tracking-wide">{system.highlight}</span>
              </div>
            )}
            <p className="text-xl text-white/70 max-w-4xl leading-relaxed">
              {system.description}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {/* Left Column: System Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <div className="lg:sticky lg:top-32">
                <div className="aspect-square bg-gradient-to-br from-[#00FFD1]/10 to-transparent border border-[#00FFD1]/30 rounded-lg flex items-center justify-center mb-6 overflow-hidden p-8">
                  {system.images && system.images[0] && !system.images[0].startsWith('/images/') ? (
                    <img 
                      src={system.images[0]} 
                      alt={system.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<svg class="text-[#00FFD1]/40" width="120" height="120" fill="currentColor"><rect width="120" height="120" rx="8"/></svg>';
                      }}
                    />
                  ) : (
                    <Layers size={120} className="text-[#00FFD1]/40" />
                  )}
                </div>
                
                {/* Quick Info Card */}
                <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-white mb-4">System Overview</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-[#00FFD1] font-mono block mb-1">Category</span>
                      <span className="text-white/80">{system.category}</span>
                    </div>
                    {system.applications && (
                      <div>
                        <span className="text-[#00FFD1] font-mono block mb-1">Applications</span>
                        <span className="text-white/80">{system.applications.length} use cases</span>
                      </div>
                    )}
                    {system.components && (
                      <div>
                        <span className="text-[#00FFD1] font-mono block mb-1">Components</span>
                        <span className="text-white/80">{system.components.length} included</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column: Detailed Info */}
            <div className="lg:col-span-2 space-y-8">
              {/* Full Description */}
              {system.fullDescription && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8"
                >
                  <h2 className="text-3xl font-bold text-white mb-4">Overview</h2>
                  <p className="text-white/70 leading-relaxed">
                    {system.fullDescription}
                  </p>
                </motion.div>
              )}

              {/* Features */}
              {system.features && system.features.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8"
                >
                  <h2 className="text-3xl font-bold text-white mb-6">Key Features</h2>
                  <ul className="grid grid-cols-1 gap-4">
                    {system.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check size={20} className="text-[#00FFD1] mt-0.5 flex-shrink-0" />
                        <span className="text-white/80">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}

              {/* PIET — Professional Image Enhancement Technology */}
              {system.piet && system.piet.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-[#0a0a0a] border border-[#00FFD1]/25 rounded-lg p-8"
                  data-testid={`system-piet-${system.slug}`}
                >
                  <div className="inline-block px-3 py-1 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full mb-3">
                    <span className="text-[#00FFD1] text-xs font-mono">// PIET Technology</span>
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-2">Professional Image Enhancement Technology</h2>
                  <p className="text-white/60 mb-6">Three situational visualisation modes — available in HD &amp; 4K and across 2D &amp; 3D endoscopy.</p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {system.piet.map((p, idx) => (
                      <div key={idx} className="bg-black/50 border border-white/10 rounded-lg p-5 hover:border-[#00FFD1]/40 transition-colors">
                        <p className="text-[#00FFD1] font-mono text-sm mb-2">{p.mode}</p>
                        <p className="text-white/70 text-sm leading-relaxed">{p.desc}</p>
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/technology/piet"
                    className="inline-flex items-center gap-1.5 mt-6 text-[#00FFD1] text-sm font-mono hover:underline"
                    data-testid={`system-piet-learn-more-${system.slug}`}
                  >
                    Learn more about PIET <ArrowRight size={14} />
                  </Link>
                </motion.div>
              )}

              {/* DiVAS Software Modules */}
              {system.divasModules && system.divasModules.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8"
                  data-testid={`system-divas-${system.slug}`}
                >
                  <h2 className="text-3xl font-bold text-white mb-2">DiVAS Software Modules</h2>
                  <p className="text-white/60 mb-6">Modular recording, analysis, documentation and archiving — add only the modules you need.</p>
                  <div className="flex flex-wrap gap-2">
                    {system.divasModules.map((m, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-[#00FFD1]/5 border border-[#00FFD1]/20 rounded-full text-white/75 text-xs font-mono">{m}</span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Components */}
              {system.components && system.components.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8"
                >
                  <h2 className="text-3xl font-bold text-white mb-6">System Components</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {system.components.map((component, idx) => {
                      const componentData = typeof component === 'string' 
                        ? { name: component, link: null } 
                        : component;
                      
                      return componentData.link ? (
                        <Link
                          key={idx}
                          to={componentData.link}
                          className="bg-black/50 border border-white/10 rounded-lg p-4 flex items-start gap-3 hover:border-[#00FFD1]/50 hover:bg-[#00FFD1]/5 transition-all group"
                        >
                          <Package size={20} className="text-[#00FFD1] mt-0.5 flex-shrink-0" />
                          <span className="text-white/80 text-sm group-hover:text-[#00FFD1] transition-colors">
                            {componentData.name}
                          </span>
                          <ArrowRight size={16} className="text-[#00FFD1]/50 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                      ) : (
                        <div
                          key={idx}
                          className="bg-black/50 border border-white/10 rounded-lg p-4 flex items-start gap-3"
                        >
                          <Package size={20} className="text-[#00FFD1] mt-0.5 flex-shrink-0" />
                          <span className="text-white/80 text-sm">{componentData.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Applications */}
              {system.applications && system.applications.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8"
                >
                  <h2 className="text-3xl font-bold text-white mb-6">Applications</h2>
                  <div className="flex flex-wrap gap-3">
                    {system.applications.map((app, idx) => (
                      <div
                        key={idx}
                        className="px-4 py-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full"
                      >
                        <span className="text-[#00FFD1] text-sm">{app}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* System Gallery — clickable, informative component shots */}
              {system.images && system.images.length > 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-[#0a0a0a] border border-white/10 rounded-lg p-8"
                  data-testid={`system-gallery-${system.slug}`}
                >
                  <h2 className="text-3xl font-bold text-white mb-2">System Gallery</h2>
                  <p className="text-white/60 mb-6">Key components of this system — select any item to view the product.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {system.images.slice(1).map((img, idx) => {
                      const meta = PRODUCT_IMAGE_INDEX[img];
                      const media = (
                        <div className="aspect-square bg-gradient-to-br from-[#00FFD1]/5 to-transparent flex items-center justify-center overflow-hidden p-4 border-b border-white/10">
                          <img
                            src={img}
                            alt={meta ? meta.name : `${system.name} detail ${idx + 1}`}
                            loading="lazy"
                            className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                            onError={(e) => { const c = e.target.closest('[data-gallery-item]'); if (c) c.style.display = 'none'; }}
                          />
                        </div>
                      );
                      return meta ? (
                        <Link
                          key={idx}
                          to={meta.path}
                          data-gallery-item
                          data-testid={`system-gallery-img-${system.slug}-${idx}`}
                          className="group block bg-black/40 border border-white/10 rounded-lg overflow-hidden hover:border-[#00FFD1]/50 transition-colors"
                        >
                          {media}
                          <div className="px-3 py-3">
                            <p className="text-white/85 text-sm font-medium leading-snug group-hover:text-[#00FFD1] transition-colors">{meta.name}</p>
                            <span className="mt-1 inline-flex items-center gap-1 text-[#00FFD1] text-xs font-mono opacity-70 group-hover:opacity-100 transition-opacity">View product <ArrowRight size={12} /></span>
                          </div>
                        </Link>
                      ) : (
                        <div
                          key={idx}
                          data-gallery-item
                          data-testid={`system-gallery-img-${system.slug}-${idx}`}
                          className="bg-black/40 border border-white/10 rounded-lg overflow-hidden"
                        >
                          {media}
                          <div className="px-3 py-3">
                            <p className="text-white/70 text-sm font-medium leading-snug">{system.name} detail</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* High-visibility brochure / quotation CTA */}
              <BrochureCTA className="mb-6" productName={system.name} />

              {/* CTA Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#00FFD1]/10 to-transparent border-2 border-[#00FFD1]/50 rounded-lg p-8"
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  Available in the Philippines
                </h2>
                <p className="text-white/70 mb-6">
                  {productsData.distributor.statement}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact-ph" state={{ productName: system.name }} className="btn-primary flex-1 justify-center">
                    Request System Quote
                    <ArrowRight size={16} />
                  </Link>
                  <Link to="/system-solutions" className="btn-secondary flex-1 justify-center">
                    <ArrowLeft size={16} />
                    Back to Systems
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default SystemDetailPage;
